import type { Task, Automation, AccountTeam, Workspace, RoleDef, RoleMapping, RiskAgent, RiskFinding, SuccessPlan, Playbook, PlaybookRun, PlaybookRunStep, Notification, RenewalOpportunity, Survey, SurveyResponse, SurveyType, SurveyStatus, CsmScorecard, ApiKey, WebhookEndpoint, WebhookDelivery, WebhookEvent, QbrDeck, SyncRun, Connector, Layout } from "./types";
import { computeScorecards } from "./scorecards";
import { buildQbr } from "./qbr";
import { predictAll, predictAccount, type Prediction } from "./predict";
export type { Prediction, Driver } from "./predict";
import { buildCockpit, type Cockpit } from "./cockpit";
export type { Cockpit, WorkItem, WorkKind, Urgency } from "./cockpit";
import { applyHealthModel, DEFAULT_HEALTH_MODEL, type ModelImpact } from "./healthmodel";
import type { HealthModel, Note } from "./types";
export type { ModelImpact, AccountProjection } from "./healthmodel";
import { buildActivity, type ActivityEntry } from "./activity";
export type { ActivityEntry, ActivityKind, ActivityTone } from "./activity";
import type { ChatChannel, ChatDelivery, ChatPlatform, ChatEvent } from "./types";
import { answerCopilotFallback, type CopilotReply, type CopilotAction } from "./copilot";
export type { CopilotReply, CopilotAction, CopilotActionKind } from "./copilot";
import type { TaskType, TaskField, TaskFieldValue, PermissionGroup, User } from "./types";
import { missingRequiredFields, applyCompletion } from "./taskTypes";
import { scheduleRunSteps, stepState } from "./schedule";
import { buildPortalView } from "./portal";
import type { PortalShare, PortalView } from "./types";
import { proposeTasks, materialize, type TaskInput } from "./automation";
import { evaluateAgent } from "./riskagent";

/** Everything about an automation the UI supplies on create (server fills the rest). */
export type AutomationInput = Pick<Automation, "name" | "trigger" | "conditions" | "actions"> &
  Partial<Pick<Automation, "description" | "enabled">>;

/* Persistence for tasks, automations, and account teams.
 *
 * Mirrors the reportClient pattern: talk to the Penumbrai API when reachable
 * (Postgres-backed, the real thing), and fall back to localStorage + the client
 * automation engine when it isn't, so the standalone demo still creates,
 * assigns, and automates end to end. The `mode` the UI shows reflects the path. */

export type Mode = "api" | "local";
const API = (import.meta.env.VITE_API_URL as string | undefined) || "/api";
const LS_TASKS = "penumbrai:tasks";
const LS_AUTOS = "penumbrai:automations";
const LS_TEAMS = "penumbrai:teams";
const LS_ROLES = "penumbrai:roles";
const LS_AGENTS = "penumbrai:riskAgents";
const LS_PLANS = "penumbrai:successPlans";
const LS_NTF = "penumbrai:notifications";
const LS_RUNS = "penumbrai:playbookRuns";
const LS_PB = "penumbrai:playbookOverrides";
const LS_PLAYBOOKS = "penumbrai:playbookTemplates";

async function req<T>(path: string, opts?: RequestInit, timeoutMs = 4000): Promise<T | null> {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), timeoutMs);
    const res = await fetch(`${API}${path}`, { credentials: "include", headers: { "content-type": "application/json" }, signal: ctrl.signal, ...opts });
    clearTimeout(t);
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

function read<T>(key: string, seed: T[]): T[] {
  try { const raw = localStorage.getItem(key); if (raw) return JSON.parse(raw) as T[]; } catch { /* ignore */ }
  return seed;
}
function write<T>(key: string, rows: T[]) { try { localStorage.setItem(key, JSON.stringify(rows)); } catch { /* ignore */ } }
const uid = (p: string) => `${p}_${Date.now().toString(36)}${Math.floor(Math.random() * 1e4).toString(36)}`;

// ---------------------------------------------------------------- tasks ----
export async function loadTasks(ws: Workspace): Promise<{ mode: Mode; tasks: Task[] }> {
  const api = await req<Task[]>("/tasks");
  if (api) return { mode: "api", tasks: api };
  return { mode: "local", tasks: read(LS_TASKS, ws.tasks) };
}
export async function createTask(mode: Mode, ws: Workspace, input: TaskInput): Promise<Task> {
  if (mode === "api") { const t = await req<Task>("/tasks", { method: "POST", body: JSON.stringify(input) }); if (t) return t; }
  const t = materialize(ws, input, uid("task"));
  write(LS_TASKS, [...read(LS_TASKS, ws.tasks), t]);
  return t;
}
export async function updateTask(mode: Mode, ws: Workspace, id: string, patch: Partial<Task>, actorId?: string): Promise<Task | null> {
  if (mode === "api") {
    const body = actorId ? { ...patch, actingUserId: actorId } : patch;
    const t = await req<Task>(`/tasks/${id}`, { method: "PATCH", body: JSON.stringify(body) });
    if (t) return t;
  }
  const all = read(LS_TASKS, ws.tasks);
  const i = all.findIndex((x) => x.id === id);
  if (i < 0) return null;
  // stamp who completed it + reassign ownership when this update finishes the task
  const finalPatch = applyCompletion(all[i], patch, actorId, new Date().toISOString());
  all[i] = { ...all[i], ...finalPatch };
  if (finalPatch.assignee || finalPatch.accountId) all[i].ownerId = resolveLocal(ws, all[i].accountId, all[i].assignee);
  write(LS_TASKS, all);
  return all[i];
}
export async function deleteTask(mode: Mode, ws: Workspace, id: string): Promise<void> {
  if (mode === "api") { await req(`/tasks/${id}`, { method: "DELETE" }); return; }
  write(LS_TASKS, read(LS_TASKS, ws.tasks).filter((t) => t.id !== id));
}
/** Save completion-field values on a task without marking it done (merges). */
export async function saveTaskFields(mode: Mode, ws: Workspace, task: Task, fields: Record<string, TaskFieldValue>): Promise<Task | null> {
  const merged = { ...task.fields, ...fields };
  return updateTask(mode, ws, task.id, { fields: merged });
}
/** Complete a task, enforcing the type's required-field gate on both sides.
 *  Returns { ok:false, missing } when the gate blocks it. */
export async function completeTask(mode: Mode, ws: Workspace, task: Task, fields: Record<string, TaskFieldValue> = {}, actorId?: string): Promise<{ ok: true; task: Task } | { ok: false; missing: TaskField[] }> {
  const type = task.typeId ? (read(LS_TASK_TYPES, ws.taskTypes).find((t) => t.id === task.typeId) ?? ws.taskTypes.find((t) => t.id === task.typeId)) : undefined;
  const merged = { ...task.fields, ...fields };
  const missing = missingRequiredFields(type, merged);
  if (missing.length) return { ok: false, missing };
  const t = await updateTask(mode, ws, task.id, { status: "done", fields: merged }, actorId);
  return t ? { ok: true, task: t } : { ok: false, missing: [] };
}

// ------------------------------------------------------------ task types ----
const LS_TASK_TYPES = "penumbrai:taskTypes";
export async function loadTaskTypes(ws: Workspace): Promise<{ mode: Mode; types: TaskType[] }> {
  const api = await req<TaskType[]>("/task-types");
  return api ? { mode: "api", types: api } : { mode: "local", types: read(LS_TASK_TYPES, ws.taskTypes) };
}
export async function createTaskType(mode: Mode, ws: Workspace, input: { name: string; description?: string; icon?: string; fields: TaskField[] }): Promise<TaskType | null> {
  if (mode === "api") return req<TaskType>("/task-types", { method: "POST", body: JSON.stringify(input) });
  const t: TaskType = { id: uid("tt"), name: input.name, description: input.description, icon: input.icon, fields: input.fields };
  write(LS_TASK_TYPES, [t, ...read(LS_TASK_TYPES, ws.taskTypes)]);
  return t;
}
export async function updateTaskType(mode: Mode, ws: Workspace, id: string, patch: Partial<Pick<TaskType, "name" | "description" | "icon" | "fields">>): Promise<TaskType | null> {
  if (mode === "api") return req<TaskType>(`/task-types/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
  const rows = read(LS_TASK_TYPES, ws.taskTypes).map((t) => t.id === id ? { ...t, ...patch } : t);
  write(LS_TASK_TYPES, rows);
  return rows.find((t) => t.id === id) ?? null;
}
export async function deleteTaskType(mode: Mode, ws: Workspace, id: string): Promise<boolean> {
  if (mode === "api") { await req(`/task-types/${id}`, { method: "DELETE" }); return true; }
  const t = read(LS_TASK_TYPES, ws.taskTypes).find((x) => x.id === id);
  if (t?.system) return false;
  write(LS_TASK_TYPES, read(LS_TASK_TYPES, ws.taskTypes).filter((x) => x.id !== id));
  return true;
}

// ---------------------------------------------------- permission groups ----
const LS_PERM_GROUPS = "penumbrai:permissionGroups";
const LS_USER_GROUPS = "penumbrai:userGroups"; // { [userId]: groupId } overrides in local mode
const readUserGroups = (): Record<string, string> => {
  try { return JSON.parse(localStorage.getItem(LS_USER_GROUPS) || "{}"); } catch { return {}; }
};
const writeUserGroups = (o: Record<string, string>) => {
  try { localStorage.setItem(LS_USER_GROUPS, JSON.stringify(o)); } catch { /* ignore */ }
};
export async function loadPermissionGroups(ws: Workspace): Promise<{ mode: Mode; groups: PermissionGroup[] }> {
  const api = await req<PermissionGroup[]>("/permission-groups");
  return api ? { mode: "api", groups: api } : { mode: "local", groups: read(LS_PERM_GROUPS, ws.permissionGroups) };
}
export async function createPermissionGroup(mode: Mode, ws: Workspace, input: { name: string; description?: string; capabilities: string[] }): Promise<PermissionGroup | null> {
  if (mode === "api") return req<PermissionGroup>("/permission-groups", { method: "POST", body: JSON.stringify(input) });
  const g: PermissionGroup = { id: uid("pg"), name: input.name, description: input.description, capabilities: input.capabilities };
  write(LS_PERM_GROUPS, [...read(LS_PERM_GROUPS, ws.permissionGroups), g]);
  return g;
}
export async function updatePermissionGroup(mode: Mode, ws: Workspace, id: string, patch: Partial<Pick<PermissionGroup, "name" | "description" | "capabilities">>): Promise<PermissionGroup | null> {
  if (mode === "api") return req<PermissionGroup>(`/permission-groups/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
  const rows = read(LS_PERM_GROUPS, ws.permissionGroups).map((g) => g.id === id ? { ...g, ...patch } : g);
  write(LS_PERM_GROUPS, rows);
  return rows.find((g) => g.id === id) ?? null;
}
export async function deletePermissionGroup(mode: Mode, ws: Workspace, id: string): Promise<boolean> {
  if (mode === "api") { await req(`/permission-groups/${id}`, { method: "DELETE" }); return true; }
  const g = read(LS_PERM_GROUPS, ws.permissionGroups).find((x) => x.id === id);
  if (g?.system) return false;
  write(LS_PERM_GROUPS, read(LS_PERM_GROUPS, ws.permissionGroups).filter((x) => x.id !== id));
  return true;
}
export async function setUserGroup(mode: Mode, ws: Workspace, userId: string, groupId: string): Promise<User | null> {
  if (mode === "api") return req<User>(`/users/${userId}/group`, { method: "PATCH", body: JSON.stringify({ groupId }) });
  const overrides = readUserGroups();
  overrides[userId] = groupId;
  writeUserGroups(overrides);
  const u = ws.users.find((x) => x.id === userId);
  return u ? { ...u, groupId } : null;
}
/** Merge locally-stored user→group overrides onto the workspace users (local mode). */
export function applyUserGroupOverrides(ws: Workspace): User[] {
  const overrides = readUserGroups();
  if (!Object.keys(overrides).length) return ws.users;
  return ws.users.map((u) => overrides[u.id] ? { ...u, groupId: overrides[u.id] } : u);
}

// ---------------------------------------------------------- automations ----
export async function loadAutomations(ws: Workspace): Promise<{ mode: Mode; automations: Automation[] }> {
  const api = await req<Automation[]>("/automations");
  if (api) return { mode: "api", automations: api };
  return { mode: "local", automations: read(LS_AUTOS, ws.automations) };
}
export async function createAutomation(mode: Mode, ws: Workspace, input: AutomationInput): Promise<Automation> {
  if (mode === "api") { const a = await req<Automation>("/automations", { method: "POST", body: JSON.stringify(input) }); if (a) return a; }
  const a: Automation = { id: uid("auto"), createdAt: new Date().toISOString().slice(0, 10), createdTaskCount: 0, ...input, enabled: input.enabled ?? true, conditions: input.conditions ?? [], actions: input.actions ?? [] };
  write(LS_AUTOS, [...read(LS_AUTOS, ws.automations), a]);
  return a;
}
export async function updateAutomation(mode: Mode, ws: Workspace, id: string, patch: Partial<Automation>): Promise<Automation | null> {
  if (mode === "api") { const a = await req<Automation>(`/automations/${id}`, { method: "PATCH", body: JSON.stringify(patch) }); if (a) return a; }
  const all = read(LS_AUTOS, ws.automations);
  const i = all.findIndex((x) => x.id === id);
  if (i < 0) return null;
  all[i] = { ...all[i], ...patch };
  write(LS_AUTOS, all);
  return all[i];
}
export async function deleteAutomation(mode: Mode, ws: Workspace, id: string): Promise<void> {
  if (mode === "api") { await req(`/automations/${id}`, { method: "DELETE" }); return; }
  write(LS_AUTOS, read(LS_AUTOS, ws.automations).filter((a) => a.id !== id));
}

/** Run rules and return the tasks that were created. */
export async function runAutomations(mode: Mode, ws: Workspace, existing: Task[], onlyId?: string): Promise<Task[]> {
  if (mode === "api") {
    const r = await req<{ created: Task[] }>("/automations/run", { method: "POST", body: JSON.stringify(onlyId ? { automationId: onlyId } : {}) });
    if (r) return r.created;
  }
  const snapshot: Workspace = { ...ws, tasks: existing, automations: read(LS_AUTOS, ws.automations) };
  const created = proposeTasks(snapshot, existing, onlyId).map((p) => materialize(ws, p.input, uid("task")));
  write(LS_TASKS, [...read(LS_TASKS, ws.tasks), ...created]);
  return created;
}

// --------------------------------------------------------------- teams ----
export async function setTeam(mode: Mode, ws: Workspace, accountId: string, roles: Record<string, string>): Promise<AccountTeam> {
  if (mode === "api") { const t = await req<AccountTeam>(`/accounts/${accountId}/team`, { method: "PUT", body: JSON.stringify({ roles }) }); if (t) return t; }
  const all = read<AccountTeam>(LS_TEAMS, ws.teams);
  const i = all.findIndex((t) => t.accountId === accountId);
  const team: AccountTeam = { accountId, roles: { ...(i >= 0 ? all[i].roles : ws.teams.find((t) => t.accountId === accountId)?.roles ?? {}), ...roles } };
  if (i >= 0) all[i] = team; else all.push(team);
  write(LS_TEAMS, all);
  return team;
}

// ------------------------------------------------ roles + entitlements ----
// The only two à la carte levers. Roles/permissions are included (unlimited);
// AI analyses are a hard monthly cap (pause, no overage).
export type EntKind = "agents" | "automations";
export const SLOT_PRICES: Record<EntKind, number> = { agents: 20, automations: 8 };
export interface Entitlement {
  roleLimit: number; agentLimit: number; automationLimit: number; aiRunsLimit: number; aiRunsUsed: number;
  rolesUsed: number; agentsUsed: number; automationsUsed: number;
  prices: Record<EntKind, number>;
}
export type RoleInput = { label: string; scope?: RoleDef["scope"]; description?: string; mapping?: RoleMapping };

const LS_ENT = "penumbrai:entitlements"; // local overrides for bought limits
type EntOverride = Partial<Pick<Entitlement, "roleLimit" | "agentLimit" | "automationLimit">>;
function entOverride(): EntOverride { try { const r = localStorage.getItem(LS_ENT); if (r) return JSON.parse(r); } catch { /* ignore */ } return {}; }
function writeEnt(o: EntOverride) { try { localStorage.setItem(LS_ENT, JSON.stringify(o)); } catch { /* ignore */ } }
const LIMIT_FIELD: Record<EntKind, "agentLimit" | "automationLimit"> = { agents: "agentLimit", automations: "automationLimit" };

/** Full usage snapshot from local state (offline mirror of GET /entitlements). */
export function localEntitlement(ws: Workspace): Entitlement {
  const o = entOverride(), base = ws.entitlements;
  return {
    roleLimit: o.roleLimit ?? base.roleLimit, agentLimit: o.agentLimit ?? base.agentLimit, automationLimit: o.automationLimit ?? base.automationLimit,
    aiRunsLimit: base.aiRunsLimit, aiRunsUsed: base.aiRunsUsed,
    rolesUsed: localRoles(ws).length, agentsUsed: read(LS_AGENTS, ws.riskAgents).length, automationsUsed: read(LS_AUTOS, ws.automations).length,
    prices: SLOT_PRICES,
  };
}
export async function loadEntitlements(ws: Workspace): Promise<{ mode: Mode; ent: Entitlement }> {
  const api = await req<Entitlement>("/entitlements");
  return api ? { mode: "api", ent: api } : { mode: "local", ent: localEntitlement(ws) };
}
export async function buySlots(mode: Mode, ws: Workspace, kind: EntKind, count = 1): Promise<Entitlement> {
  if (mode === "api") { const e = await req<Entitlement>("/entitlements/buy", { method: "POST", body: JSON.stringify({ kind, count }) }); if (e && "roleLimit" in e) return e; }
  const o = entOverride(); const cur = localEntitlement(ws)[LIMIT_FIELD[kind]];
  writeEnt({ ...o, [LIMIT_FIELD[kind]]: cur + count });
  return localEntitlement(ws);
}

const slug = (label: string, existing: Set<string>): string => {
  const base = label.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "role";
  let k = base, i = 2;
  while (existing.has(k)) k = `${base}_${i++}`;
  return k;
};
function localRoles(ws: Workspace): RoleDef[] { return read(LS_ROLES, ws.roles); }
const localLimit = (ws: Workspace): number => localEntitlement(ws).roleLimit;

export async function loadRoles(ws: Workspace): Promise<{ mode: Mode; roles: RoleDef[]; entitlement: Entitlement }> {
  const [roles, ent] = await Promise.all([req<RoleDef[]>("/roles"), req<Entitlement>("/entitlements")]);
  if (roles && ent) return { mode: "api", roles, entitlement: ent };
  return { mode: "local", roles: localRoles(ws), entitlement: localEntitlement(ws) };
}
export async function createRole(mode: Mode, ws: Workspace, input: RoleInput): Promise<{ ok: true; role: RoleDef } | { ok: false; limit: number }> {
  if (mode === "api") {
    try {
      const ctrl = new AbortController(); const t = setTimeout(() => ctrl.abort(), 1800);
      const res = await fetch(`${API}/roles`, { method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify(input), signal: ctrl.signal });
      clearTimeout(t);
      if (res.status === 402) { const b = await res.json(); return { ok: false, limit: b.limit ?? 0 }; }
      if (res.ok) return { ok: true, role: (await res.json()) as RoleDef };
    } catch { /* fall through to local */ }
  }
  const roles = localRoles(ws);
  if (roles.length >= localLimit(ws)) return { ok: false, limit: localLimit(ws) };
  const role: RoleDef = { key: slug(input.label, new Set(roles.map((r) => r.key))), label: input.label, scope: input.scope ?? "account", description: input.description, mapping: input.mapping ?? { type: "manual" }, system: false };
  write(LS_ROLES, [...roles, role]);
  return { ok: true, role };
}
export async function updateRole(mode: Mode, ws: Workspace, key: string, patch: Partial<RoleDef>): Promise<RoleDef | null> {
  if (mode === "api") { const r = await req<RoleDef>(`/roles/${key}`, { method: "PATCH", body: JSON.stringify(patch) }); if (r) return r; }
  const roles = localRoles(ws);
  const i = roles.findIndex((r) => r.key === key);
  if (i < 0) return null;
  roles[i] = { ...roles[i], ...patch };
  write(LS_ROLES, roles);
  return roles[i];
}
export async function deleteRole(mode: Mode, ws: Workspace, key: string): Promise<void> {
  if (mode === "api") { await req(`/roles/${key}`, { method: "DELETE" }); return; }
  write(LS_ROLES, localRoles(ws).filter((r) => r.key !== key));
}

// --------------------------------------------------------- risk agents ----
export interface AgentInput { name: string; description?: string; enabled?: boolean; nodes?: RiskAgent["nodes"]; edges?: RiskAgent["edges"]; }
export interface RunResult { findings: RiskFinding[]; signalsRaised: number; tasksCreated: number; notifications: number; }
const localAgents = (ws: Workspace): RiskAgent[] => read(LS_AGENTS, ws.riskAgents);

export async function loadRiskAgents(ws: Workspace): Promise<{ mode: Mode; agents: RiskAgent[] }> {
  const api = await req<RiskAgent[]>("/risk-agents");
  if (api) return { mode: "api", agents: api };
  return { mode: "local", agents: localAgents(ws) };
}
export async function loadSources(ws: Workspace): Promise<{ id: string; label: string; unstructured: boolean }[]> {
  const api = await req<{ id: string; label: string; unstructured: boolean }[]>("/risk-agents/sources");
  if (api) return api;
  const out = [{ id: "accounts", label: "Accounts", unstructured: false }, { id: "signals", label: "Signals", unstructured: false }];
  for (const o of ws.objects) if (o.apiName !== "account" && (ws.records?.[o.apiName]?.length ?? 0) > 0) out.push({ id: o.apiName, label: o.labelPlural, unstructured: o.kind === "unstructured" });
  return out;
}
export async function createRiskAgent(mode: Mode, ws: Workspace, input: AgentInput): Promise<RiskAgent> {
  if (mode === "api") { const a = await req<RiskAgent>("/risk-agents", { method: "POST", body: JSON.stringify(input) }); if (a) return a; }
  const a: RiskAgent = { id: uid("risk"), name: input.name, description: input.description, enabled: input.enabled ?? false, nodes: input.nodes ?? [], edges: input.edges ?? [], createdAt: new Date().toISOString().slice(0, 10), findingsCount: 0, signalsRaised: 0 };
  write(LS_AGENTS, [...localAgents(ws), a]);
  return a;
}
export async function updateRiskAgent(mode: Mode, ws: Workspace, id: string, patch: Partial<RiskAgent>): Promise<RiskAgent | null> {
  if (mode === "api") { const a = await req<RiskAgent>(`/risk-agents/${id}`, { method: "PATCH", body: JSON.stringify(patch) }); if (a) return a; }
  const all = localAgents(ws);
  const i = all.findIndex((x) => x.id === id);
  if (i < 0) return null;
  all[i] = { ...all[i], ...patch };
  write(LS_AGENTS, all);
  return all[i];
}
export async function deleteRiskAgent(mode: Mode, ws: Workspace, id: string): Promise<void> {
  if (mode === "api") { await req(`/risk-agents/${id}`, { method: "DELETE" }); return; }
  write(LS_AGENTS, localAgents(ws).filter((a) => a.id !== id));
}
/** Findings an agent would surface right now. Prefers the server engine; else
 * evaluates locally over the passed-in (possibly edited) agent. */
export async function previewAgent(mode: Mode, ws: Workspace, agent: RiskAgent): Promise<RiskFinding[]> {
  if (mode === "api") { const f = await req<RiskFinding[]>(`/risk-agents/${agent.id}/preview`); if (f) return f; }
  return evaluateAgent({ ...ws, riskAgents: [agent] }, agent);
}
export async function runRiskAgent(mode: Mode, ws: Workspace, agent: RiskAgent): Promise<RunResult> {
  if (mode === "api") { const r = await req<RunResult>(`/risk-agents/${agent.id}/run`, { method: "POST" }); if (r) return r; }
  // offline: create tasks locally, surface findings; signals raised are counted.
  const findings = evaluateAgent({ ...ws, riskAgents: [agent] }, agent);
  const byId = new Map(agent.nodes.map((n) => [n.id, n] as const));
  let tasksCreated = 0, signalsRaised = 0;
  for (const det of agent.nodes.filter((n) => n.kind === "detector")) {
    const acts = agent.edges.filter((e) => e.from === det.id).map((e) => byId.get(e.to)).filter(Boolean);
    for (const f of findings.filter((x) => x.detectorId === det.id)) for (const act of acts) {
      if (act!.kind === "action" && act!.type === "create_task") { await createTask(mode, ws, { title: act!.title.replace("{account}", f.accountName), accountId: f.accountId, assignee: act!.assignee, due: "2026-08-24", priority: act!.priority, source: "automation" }); tasksCreated++; }
      if (act!.kind === "action" && act!.type === "raise_signal") signalsRaised++;
    }
  }
  return { findings, signalsRaised, tasksCreated, notifications: 0 };
}

// -------------------------------------------------------- success plans ----
export type PlanInput = { accountId: string; title: string; outcome?: string } & Partial<Pick<SuccessPlan, "status" | "objectives" | "targetDate">>;
const rollup = (objs: SuccessPlan["objectives"]): SuccessPlan["status"] =>
  objs.length === 0 ? "draft" : objs.every((o) => o.status === "done") ? "achieved" : objs.some((o) => o.status === "blocked") ? "at_risk" : "on_track";
const localPlans = (ws: Workspace): SuccessPlan[] => read(LS_PLANS, ws.successPlans);

export async function loadPlans(ws: Workspace): Promise<{ mode: Mode; plans: SuccessPlan[] }> {
  const api = await req<SuccessPlan[]>("/success-plans");
  return api ? { mode: "api", plans: api } : { mode: "local", plans: localPlans(ws) };
}
export async function createPlan(mode: Mode, ws: Workspace, input: PlanInput): Promise<SuccessPlan> {
  if (mode === "api") { const p = await req<SuccessPlan>("/success-plans", { method: "POST", body: JSON.stringify(input) }); if (p) return p; }
  const account = ws.accounts.find((a) => a.id === input.accountId);
  const objectives = input.objectives ?? [];
  const p: SuccessPlan = { id: uid("plan"), accountId: input.accountId, accountName: account?.name ?? "", title: input.title, outcome: input.outcome ?? "", status: input.status ?? rollup(objectives), objectives, targetDate: input.targetDate, createdAt: new Date().toISOString().slice(0, 10), updatedAt: new Date().toISOString().slice(0, 10) };
  write(LS_PLANS, [...localPlans(ws), p]);
  return p;
}
export async function updatePlan(mode: Mode, ws: Workspace, id: string, patch: Partial<SuccessPlan>): Promise<SuccessPlan | null> {
  if (mode === "api") { const p = await req<SuccessPlan>(`/success-plans/${id}`, { method: "PATCH", body: JSON.stringify(patch) }); if (p) return p; }
  const all = localPlans(ws);
  const i = all.findIndex((x) => x.id === id);
  if (i < 0) return null;
  all[i] = { ...all[i], ...patch };
  if (patch.objectives && patch.status === undefined) all[i].status = rollup(all[i].objectives);
  write(LS_PLANS, all);
  return all[i];
}
export async function deletePlan(mode: Mode, ws: Workspace, id: string): Promise<void> {
  if (mode === "api") { await req(`/success-plans/${id}`, { method: "DELETE" }); return; }
  write(LS_PLANS, localPlans(ws).filter((p) => p.id !== id));
}

// ------------------------------------------------------------ renewals ----
const LS_RENEWALS = "penumbrai:renewals";
const localRenewals = (ws: Workspace): RenewalOpportunity[] => read(LS_RENEWALS, ws.renewals);
export async function loadRenewals(ws: Workspace): Promise<{ mode: Mode; renewals: RenewalOpportunity[] }> {
  const api = await req<RenewalOpportunity[]>("/renewals");
  return api ? { mode: "api", renewals: api } : { mode: "local", renewals: localRenewals(ws) };
}
export async function updateRenewal(mode: Mode, ws: Workspace, id: string, patch: Partial<RenewalOpportunity>): Promise<RenewalOpportunity | null> {
  if (mode === "api") { const r = await req<RenewalOpportunity>(`/renewals/${id}`, { method: "PATCH", body: JSON.stringify(patch) }); if (r) return r; }
  const all = localRenewals(ws);
  const i = all.findIndex((x) => x.id === id);
  if (i < 0) return null;
  all[i] = { ...all[i], ...patch };
  write(LS_RENEWALS, all);
  return all[i];
}

// ------------------------------------------------------- notifications ----
const localNtf = (ws: Workspace): Notification[] => read(LS_NTF, ws.notifications);
export async function loadNotifications(ws: Workspace): Promise<{ mode: Mode; notifications: Notification[] }> {
  const api = await req<Notification[]>("/notifications");
  return api ? { mode: "api", notifications: api } : { mode: "local", notifications: localNtf(ws) };
}
export async function markNotificationRead(mode: Mode, ws: Workspace, id: string): Promise<void> {
  if (mode === "api") { await req(`/notifications/${id}/read`, { method: "POST" }); return; }
  write(LS_NTF, localNtf(ws).map((n) => (n.id === id ? { ...n, read: true } : n)));
}
export async function markAllNotificationsRead(mode: Mode, ws: Workspace): Promise<void> {
  if (mode === "api") { await req("/notifications/read-all", { method: "POST" }); return; }
  write(LS_NTF, localNtf(ws).map((n) => ({ ...n, read: true })));
}

// --------------------------------------------------------------- import ----
export interface ImportRowResult { index: number; ok: boolean; id?: string; errors?: { field: string; message: string }[]; }
export interface ImportSummary { created: number; failed: number; results: ImportRowResult[]; }

/** Bulk-import mapped rows into an object. In API mode the server validates
 * and persists; in local mode we validate client-side and report (the demo
 * store is read-only), so the preview and result are honest either way. */
export async function importRecords(mode: Mode, object: string, mapped: { attributes: Record<string, unknown>; errors: { field: string; message: string }[] }[]): Promise<ImportSummary & { mode: Mode }> {
  if (mode === "api") {
    const rows = mapped.map((m) => m.attributes);
    const r = await req<ImportSummary>(`/records/${object}/import`, { method: "POST", body: JSON.stringify({ rows }) });
    if (r) return { ...r, mode: "api" };
  }
  // local: a row fails if client-side mapping already flagged an error
  const results: ImportRowResult[] = mapped.map((m, i) => (m.errors.length ? { index: i, ok: false, errors: m.errors } : { index: i, ok: true, id: `local_${i}` }));
  return { created: results.filter((r) => r.ok).length, failed: results.filter((r) => !r.ok).length, results, mode: "local" };
}

// ----------------------------------------------------- developer platform ----
const LS_KEYS = "penumbrai:apiKeys";
const LS_HOOKS = "penumbrai:webhooks";
const LS_DELIV = "penumbrai:webhookDeliveries";
const localKeys = (ws: Workspace): ApiKey[] => read(LS_KEYS, ws.apiKeys);
const localHooks = (ws: Workspace): WebhookEndpoint[] => read(LS_HOOKS, ws.webhooks);
const localDeliv = (ws: Workspace): WebhookDelivery[] => read(LS_DELIV, ws.webhookDeliveries);
const token = (n: number) => { const cs = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"; let s = ""; for (let i = 0; i < n; i++) s += cs[Math.floor(Math.random() * cs.length)]; return s; };

export async function loadDeveloper(ws: Workspace): Promise<{ mode: Mode; keys: ApiKey[]; webhooks: WebhookEndpoint[]; deliveries: WebhookDelivery[] }> {
  const [k, w, d] = await Promise.all([req<ApiKey[]>("/api-keys"), req<WebhookEndpoint[]>("/webhooks"), req<WebhookDelivery[]>("/webhook-deliveries")]);
  if (k && w && d) return { mode: "api", keys: k, webhooks: w, deliveries: d };
  return { mode: "local", keys: localKeys(ws), webhooks: localHooks(ws), deliveries: localDeliv(ws) };
}
export async function createApiKey(mode: Mode, ws: Workspace, name: string): Promise<{ key: ApiKey; secret: string }> {
  if (mode === "api") { const r = await req<{ key: ApiKey; secret: string }>("/api-keys", { method: "POST", body: JSON.stringify({ name }) }); if (r) return r; }
  const body = token(32);
  const key: ApiKey = { id: uid("key"), name, prefix: "pk_live", last4: body.slice(-4), createdAt: new Date().toISOString() };
  write(LS_KEYS, [key, ...localKeys(ws)]);
  return { key, secret: `pk_live_${body}` };
}
export async function revokeApiKey(mode: Mode, ws: Workspace, id: string): Promise<void> {
  if (mode === "api") { await req(`/api-keys/${id}/revoke`, { method: "POST" }); return; }
  write(LS_KEYS, localKeys(ws).map((k) => (k.id === id ? { ...k, revokedAt: new Date().toISOString() } : k)));
}
export async function createWebhook(mode: Mode, ws: Workspace, input: { url: string; events: WebhookEvent[] }): Promise<{ endpoint: WebhookEndpoint; secret: string }> {
  if (mode === "api") { const r = await req<{ endpoint: WebhookEndpoint; secret: string }>("/webhooks", { method: "POST", body: JSON.stringify(input) }); if (r) return r; }
  const body = token(32);
  const w: WebhookEndpoint = { id: uid("whk"), url: input.url, events: input.events, active: true, secretLast4: body.slice(-4), createdAt: new Date().toISOString() };
  write(LS_HOOKS, [w, ...localHooks(ws)]);
  return { endpoint: w, secret: `whsec_${body}` };
}
export async function updateWebhook(mode: Mode, ws: Workspace, id: string, patch: Partial<Pick<WebhookEndpoint, "url" | "events" | "active">>): Promise<WebhookEndpoint | null> {
  if (mode === "api") { const r = await req<WebhookEndpoint>(`/webhooks/${id}`, { method: "PATCH", body: JSON.stringify(patch) }); if (r) return r; }
  const all = localHooks(ws); const i = all.findIndex((w) => w.id === id);
  if (i < 0) return null;
  all[i] = { ...all[i], ...patch }; write(LS_HOOKS, all); return all[i];
}
export async function deleteWebhook(mode: Mode, ws: Workspace, id: string): Promise<void> {
  if (mode === "api") { await req(`/webhooks/${id}`, { method: "DELETE" }); return; }
  write(LS_HOOKS, localHooks(ws).filter((w) => w.id !== id));
  write(LS_DELIV, localDeliv(ws).filter((d) => d.endpointId !== id));
}
export async function testWebhook(mode: Mode, ws: Workspace, id: string): Promise<WebhookDelivery | null> {
  if (mode === "api") { const r = await req<WebhookDelivery>(`/webhooks/${id}/test`, { method: "POST" }); if (r) return r; }
  const w = localHooks(ws).find((x) => x.id === id);
  if (!w) return null;
  const status: WebhookDelivery["status"] = w.active ? "success" : "failed";
  const d: WebhookDelivery = { id: uid("whd"), endpointId: id, event: w.events[0] ?? "account.health_changed", status, responseCode: status === "success" ? 200 : 0, at: new Date().toISOString() };
  write(LS_DELIV, [d, ...localDeliv(ws)]);
  return d;
}

// ----------------------------------------------------------- scorecards ----
export async function loadScorecards(ws: Workspace): Promise<{ mode: Mode; scorecards: CsmScorecard[] }> {
  const api = await req<CsmScorecard[]>("/scorecards");
  return api ? { mode: "api", scorecards: api } : { mode: "local", scorecards: computeScorecards(ws) };
}

// ------------------------------------------------------------------ layouts ----
const LS_LAYOUTS = "penumbrai:layouts";
const localLayouts = (ws: Workspace): Layout[] => read(LS_LAYOUTS, ws.layouts);

export async function loadLayouts(ws: Workspace): Promise<{ mode: Mode; layouts: Layout[] }> {
  const api = await req<Layout[]>("/layouts");
  return api ? { mode: "api", layouts: api } : { mode: "local", layouts: localLayouts(ws) };
}
export async function saveLayout(mode: Mode, ws: Workspace, id: string, sections: Layout["sections"]): Promise<Layout | null> {
  if (mode === "api") { const l = await req<Layout>(`/layouts/${id}`, { method: "PUT", body: JSON.stringify({ sections }) }); if (l) return l; }
  const all = localLayouts(ws);
  const i = all.findIndex((l) => l.id === id);
  if (i < 0) return null;
  all[i] = { ...all[i], sections };
  write(LS_LAYOUTS, all);
  return all[i];
}

// ----------------------------------------------------------- customer portal ----
const portalKey = (token: string) => `penumbrai:portal:${token}`;

/** Create a shareable customer-portal link for an account. In demo mode we
 * snapshot the view into localStorage so the generated link renders offline. */
export async function createShare(ws: Workspace, accountId: string): Promise<{ url: string; token: string }> {
  const r = await req<{ share: PortalShare; url: string }>(`/accounts/${accountId}/share`, { method: "POST" });
  if (r) return { url: r.url, token: r.share.token };
  const token = uid("pt").replace(/_/g, "");
  const view = buildPortalView(ws, accountId);
  try { localStorage.setItem(portalKey(token), JSON.stringify(view)); } catch { /* ignore */ }
  return { url: `${location.origin}/portal/${token}`, token };
}
/** Load a portal view by token (public). Falls back to a local snapshot. */
export async function loadPortal(token: string): Promise<PortalView | null> {
  const api = await req<PortalView>(`/portal/${token}`);
  if (api) return api;
  try { const raw = localStorage.getItem(portalKey(token)); if (raw) return JSON.parse(raw) as PortalView; } catch { /* ignore */ }
  return null;
}

// ------------------------------------------------------------- connectors ----
const LS_SYNCS = "penumbrai:syncRuns";
const localSyncs = (ws: Workspace): SyncRun[] => read(LS_SYNCS, ws.syncRuns);

export async function loadConnectors(ws: Workspace): Promise<{ mode: Mode; connectors: Connector[]; runs: SyncRun[] }> {
  const [c, r] = await Promise.all([req<Connector[]>("/connectors"), req<SyncRun[]>("/sync-runs")]);
  if (c && r) return { mode: "api", connectors: c, runs: r };
  const connectors: Connector[] = [
    { key: "warehouse_sample", label: "Warehouse (sample)", object: "usage_event", kind: "sample" },
    { key: "http_json", label: "HTTP JSON source", object: "usage_event", kind: "http_json" },
  ];
  return { mode: "local", connectors, runs: localSyncs(ws) };
}
export async function runConnector(mode: Mode, ws: Workspace, key: string, opts?: { object?: string; url?: string }): Promise<SyncRun> {
  if (mode === "api") { const r = await req<SyncRun>(`/connectors/${key}/sync`, { method: "POST", body: JSON.stringify(opts ?? {}) }); if (r) return r; }
  // local: simulate the sample connector deterministically
  const run: SyncRun = { id: uid("sync"), connectorKey: key, connectorLabel: key === "warehouse_sample" ? "Warehouse (sample)" : "HTTP JSON source", object: opts?.object ?? "usage_event", created: key === "warehouse_sample" ? 6 : 0, failed: 0, status: key === "warehouse_sample" ? "success" : "failed", message: key === "warehouse_sample" ? undefined : "Live sync requires the API", at: new Date().toISOString() };
  write(LS_SYNCS, [run, ...localSyncs(ws)]);
  return run;
}

// -------------------------------------------------------------------- ai ----
export interface AiSummary { text: string; model: string | null; source: "llm" | "fallback"; }

export async function aiStatus(): Promise<{ available: boolean }> {
  const r = await req<{ available: boolean }>("/ai/status");
  return r ?? { available: false };
}
/** Generate an account summary. Falls back to a local deterministic narrative
 * when the API is unreachable, so the button always produces something. */
export async function accountAiSummary(ws: Workspace, accountId: string): Promise<AiSummary> {
  const r = await req<AiSummary>(`/accounts/${accountId}/ai-summary`);
  if (r) return r;
  return { text: localSummary(ws, accountId), model: null, source: "fallback" };
}
function localSummary(ws: Workspace, accountId: string): string {
  const a = ws.accounts.find((x) => x.id === accountId);
  if (!a) return "Account not found.";
  const util = a.seats ? Math.round((a.seatsActive / a.seats) * 100) : 0;
  const band = a.healthBand === "good" ? "healthy" : a.healthBand === "watch" ? "needs attention" : "at risk";
  const risks = ws.signals.filter((s) => s.accountId === accountId && s.band !== "positive").map((s) => s.title);
  return `${a.name} is ${band} (health ${a.health}/100) with ${util}% seat utilization.` +
    (risks.length ? ` Watch items: ${risks.slice(0, 2).join("; ")}.` : " No open risks — a candidate for expansion.");
}

// ------------------------------------------------------------------- qbr ----
export async function loadQbr(ws: Workspace, accountId: string): Promise<{ mode: Mode; deck: QbrDeck | undefined }> {
  const api = await req<QbrDeck>(`/accounts/${accountId}/qbr`);
  return api ? { mode: "api", deck: api } : { mode: "local", deck: buildQbr(ws, accountId) };
}

// ---------------------------------------------------------- predictions ----
export async function loadPredictions(ws: Workspace): Promise<{ mode: Mode; predictions: Prediction[] }> {
  const api = await req<Prediction[]>("/predictions");
  return api ? { mode: "api", predictions: api } : { mode: "local", predictions: predictAll(ws) };
}
export async function loadPrediction(ws: Workspace, accountId: string): Promise<Prediction | undefined> {
  const api = await req<Prediction>(`/accounts/${accountId}/prediction`);
  if (api) return api;
  const a = ws.accounts.find((x) => x.id === accountId);
  return a ? predictAccount(ws, a) : undefined;
}

// ------------------------------------------------------------- cockpit ----
export async function loadCockpit(ws: Workspace, ownerId?: string): Promise<{ mode: Mode; cockpit: Cockpit }> {
  const api = await req<Cockpit>(`/cockpit${ownerId ? `?ownerId=${encodeURIComponent(ownerId)}` : ""}`);
  return api ? { mode: "api", cockpit: api } : { mode: "local", cockpit: buildCockpit(ws, { ownerId }) };
}

// -------------------------------------------------------- health model ----
const LS_HEALTH_MODEL = "penumbrai:healthModel";
type HealthModelResult = { model: HealthModel; impact: ModelImpact };
function localModel(ws: Workspace): HealthModel {
  try { const raw = localStorage.getItem(LS_HEALTH_MODEL); if (raw) return JSON.parse(raw) as HealthModel; } catch { /* ignore */ }
  return ws.healthModel ?? DEFAULT_HEALTH_MODEL;
}
export async function loadHealthModel(ws: Workspace): Promise<{ mode: Mode } & HealthModelResult> {
  const api = await req<HealthModelResult>("/health-model");
  if (api) return { mode: "api", ...api };
  const model = localModel(ws);
  return { mode: "local", model, impact: applyHealthModel(ws, model) };
}
/** Live preview for a candidate model — never persists. */
export async function previewHealthModel(mode: Mode, ws: Workspace, model: HealthModel): Promise<ModelImpact> {
  if (mode === "api") { const r = await req<HealthModelResult>("/health-model/preview", { method: "POST", body: JSON.stringify(model) }); if (r) return r.impact; }
  return applyHealthModel(ws, model);
}
export async function saveHealthModel(mode: Mode, ws: Workspace, model: HealthModel): Promise<HealthModelResult> {
  if (mode === "api") { const r = await req<HealthModelResult>("/health-model", { method: "PUT", body: JSON.stringify(model) }); if (r) return r; }
  try { localStorage.setItem(LS_HEALTH_MODEL, JSON.stringify(model)); } catch { /* ignore */ }
  return { model, impact: applyHealthModel(ws, model) };
}

// ------------------------------------------------------- activity + notes ----
const LS_NOTES = "penumbrai:notes";
function localNotes(ws: Workspace): Note[] { return read(LS_NOTES, ws.notes ?? []); }
export async function loadActivity(ws: Workspace, accountId: string): Promise<{ mode: Mode; entries: ActivityEntry[] }> {
  const api = await req<ActivityEntry[]>(`/accounts/${accountId}/activity`);
  if (api) return { mode: "api", entries: api };
  return { mode: "local", entries: buildActivity(ws, accountId, localNotes(ws)) };
}
export async function addNote(mode: Mode, ws: Workspace, accountId: string, body: string, ai = false): Promise<Note | null> {
  if (mode === "api") { const n = await req<Note>(`/accounts/${accountId}/notes`, { method: "POST", body: JSON.stringify({ body, ai }) }); if (n) return n; }
  const me = ws.users.find((u) => u.id === ws.currentUserId) ?? ws.users[0];
  const note: Note = { id: uid("note"), accountId, authorId: me?.id ?? "", authorName: me?.name ?? "You", body, at: new Date().toISOString(), ai };
  write(LS_NOTES, [note, ...localNotes(ws)]);
  return note;
}
// -------------------------------------------------- slack / teams delivery ----
const LS_CHANNELS = "penumbrai:chatChannels";
const LS_CHAT_DELIVERIES = "penumbrai:chatDeliveries";
const hostOf = (url: string) => { try { return new URL(url).host; } catch { return "invalid-url"; } };

export async function chatLive(): Promise<boolean> {
  const r = await req<{ live: boolean }>("/chat/status");
  return r?.live ?? false;
}
export async function loadChatChannels(ws: Workspace): Promise<{ mode: Mode; channels: ChatChannel[] }> {
  const api = await req<ChatChannel[]>("/chat/channels");
  return api ? { mode: "api", channels: api } : { mode: "local", channels: read(LS_CHANNELS, ws.chatChannels) };
}
export async function loadChatDeliveries(ws: Workspace): Promise<ChatDelivery[]> {
  const api = await req<ChatDelivery[]>("/chat/deliveries");
  return api ?? read(LS_CHAT_DELIVERIES, ws.chatDeliveries);
}
export async function createChatChannel(mode: Mode, ws: Workspace, input: { platform: ChatPlatform; name: string; url: string; events: ChatEvent[] }): Promise<ChatChannel | null> {
  if (mode === "api") return req<ChatChannel>("/chat/channels", { method: "POST", body: JSON.stringify(input) });
  const c: ChatChannel = { id: uid("chn"), platform: input.platform, name: input.name, urlHost: hostOf(input.url), events: input.events, active: true, createdAt: new Date().toISOString() };
  write(LS_CHANNELS, [c, ...read(LS_CHANNELS, ws.chatChannels)]);
  return c;
}
export async function updateChatChannel(mode: Mode, ws: Workspace, id: string, patch: Partial<Pick<ChatChannel, "name" | "events" | "active">>): Promise<ChatChannel | null> {
  if (mode === "api") return req<ChatChannel>(`/chat/channels/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
  const rows = read(LS_CHANNELS, ws.chatChannels).map((c) => c.id === id ? { ...c, ...patch } : c);
  write(LS_CHANNELS, rows);
  return rows.find((c) => c.id === id) ?? null;
}
export async function deleteChatChannel(mode: Mode, ws: Workspace, id: string): Promise<boolean> {
  if (mode === "api") { await req(`/chat/channels/${id}`, { method: "DELETE" }); return true; }
  write(LS_CHANNELS, read(LS_CHANNELS, ws.chatChannels).filter((c) => c.id !== id));
  return true;
}
function simulateDelivery(ws: Workspace, channel: ChatChannel, event: ChatEvent, title: string): ChatDelivery {
  const d: ChatDelivery = { id: uid("chd"), channelId: channel.id, channelName: channel.name, platform: channel.platform, event, title, status: channel.active ? "success" : "failed", responseCode: channel.active ? 200 : 0, at: new Date().toISOString() };
  write(LS_CHAT_DELIVERIES, [d, ...read(LS_CHAT_DELIVERIES, ws.chatDeliveries)].slice(0, 50));
  return d;
}
export async function testChatChannel(mode: Mode, ws: Workspace, channel: ChatChannel): Promise<ChatDelivery | null> {
  if (mode === "api") return req<ChatDelivery>(`/chat/channels/${channel.id}/test`, { method: "POST", body: "{}" });
  return simulateDelivery(ws, { ...channel, active: true }, "digest", "Test message from Penumbrai");
}
export async function sendDigest(mode: Mode, ws: Workspace, channelId: string, channel: ChatChannel): Promise<ChatDelivery | null> {
  if (mode === "api") { const r = await req<ChatDelivery[]>("/chat/digest", { method: "POST", body: JSON.stringify({ channelId }) }); return r?.[0] ?? null; }
  const c = buildCockpit(ws);
  return simulateDelivery(ws, channel, "digest", `Daily cockpit — ${c.summary.now} to do now`);
}

// ---------------------------------------------------------------- copilot ----
export async function askCopilot(ws: Workspace, question: string): Promise<CopilotReply> {
  const api = await req<CopilotReply>("/copilot/ask", { method: "POST", body: JSON.stringify({ question }) }, 20000);
  return api ?? answerCopilotFallback(ws, question);
}
export interface CopilotActResult { done: string; link?: string; }
export async function actCopilot(mode: Mode, ws: Workspace, action: CopilotAction): Promise<CopilotActResult> {
  if (mode === "api") { const r = await req<CopilotActResult>("/copilot/act", { method: "POST", body: JSON.stringify(action) }); if (r) return r; }
  // local execution mirrors the server actions
  const acct = action.accountId ? ws.accounts.find((a) => a.id === action.accountId) : undefined;
  if (action.kind === "create_task" && acct) {
    await createTask(mode, ws, { title: action.title ?? `Follow up with ${acct.name}`, accountId: acct.id, assignee: { kind: "user", userId: acct.ownerId }, due: new Date(Date.now() + 7 * 86_400_000).toISOString().slice(0, 10), priority: action.priority ?? "medium", source: "manual" });
    return { done: `Created task "${action.title ?? `Follow up with ${acct.name}`}"`, link: "/tasks" };
  }
  if (action.kind === "add_note" && acct) {
    const body = action.body?.trim() || (localDraftNote(ws, acct.id)?.body ?? `Follow-up note for ${acct.name}.`);
    await addNote(mode, ws, acct.id, body, true);
    return { done: `Drafted a note on ${acct.name}`, link: `/accounts/${acct.id}` };
  }
  if (action.kind === "send_digest") {
    const channels = read(LS_CHANNELS, ws.chatChannels).filter((c) => c.active && c.events.includes("digest"));
    channels.forEach((c) => simulateDelivery(ws, c, "digest", `Daily cockpit — ${buildCockpit(ws).summary.now} to do now`));
    return { done: channels.length ? `Digest sent to ${channels.length} channel${channels.length > 1 ? "s" : ""}` : "No channels are subscribed to the digest yet", link: "/delivery" };
  }
  return { done: "Nothing to do." };
}

export interface NoteDraft { body: string; model: string | null; source: "llm" | "fallback"; }
export async function draftNote(ws: Workspace, accountId: string): Promise<NoteDraft | null> {
  const api = await req<NoteDraft>(`/accounts/${accountId}/notes/ai-draft`, { method: "POST", body: "{}" });
  if (api) return api;
  return localDraftNote(ws, accountId);
}
/** Deterministic offline note draft — mirrors the API fallback shape. */
function localDraftNote(ws: Workspace, accountId: string): NoteDraft | null {
  const a = ws.accounts.find((x) => x.id === accountId);
  if (!a) return null;
  const util = a.seats ? Math.round((a.seatsActive / a.seats) * 100) : 0;
  const sigs = ws.signals.filter((s) => s.accountId === accountId);
  const risks = sigs.filter((s) => s.band !== "positive").map((s) => s.title);
  const wins = sigs.filter((s) => s.band === "positive").map((s) => s.title);
  const rn = ws.renewals.find((r) => r.accountId === accountId);
  const openTasks = ws.tasks.filter((t) => t.accountId === accountId && t.status !== "done").length;
  const bandWord = a.healthBand === "good" ? "healthy" : a.healthBand === "watch" ? "needs attention" : "at risk";
  const lines = [`${a.name} is ${bandWord} at health ${a.health} with ${util}% seat utilization.`];
  if (risks.length) lines.push(`Open concerns: ${risks.slice(0, 3).join("; ")}.`);
  if (wins.length) lines.push(`Bright spots: ${wins.slice(0, 2).join("; ")}.`);
  if (rn) lines.push(`Renewal ${rn.renewalDate} forecast "${rn.category}"${openTasks ? `; ${openTasks} open task(s) to clear` : ""}.`);
  lines.push(risks.length ? "Next: schedule a check-in to de-risk the renewal." : "Next: explore an expansion conversation.");
  return { body: lines.join(" "), model: null, source: "fallback" };
}

// -------------------------------------------------------------- surveys ----
const LS_SURVEYS = "penumbrai:surveys";
const LS_RESPONSES = "penumbrai:surveyResponses";
const localSurveys = (ws: Workspace): Survey[] => read(LS_SURVEYS, ws.surveys);
const localResponses = (ws: Workspace): SurveyResponse[] => read(LS_RESPONSES, ws.surveyResponses);

export async function loadSurveys(ws: Workspace): Promise<{ mode: Mode; surveys: Survey[]; responses: SurveyResponse[] }> {
  const [s, r] = await Promise.all([req<Survey[]>("/surveys"), req<SurveyResponse[]>("/survey-responses")]);
  if (s && r) return { mode: "api", surveys: s, responses: r };
  return { mode: "local", surveys: localSurveys(ws), responses: localResponses(ws) };
}
export async function createSurvey(mode: Mode, ws: Workspace, input: { name: string; type: SurveyType; question: string }): Promise<Survey> {
  if (mode === "api") { const s = await req<Survey>("/surveys", { method: "POST", body: JSON.stringify(input) }); if (s) return s; }
  const s: Survey = { id: uid("svy"), name: input.name, type: input.type, question: input.question, status: "draft", createdAt: new Date().toISOString() };
  write(LS_SURVEYS, [...localSurveys(ws), s]);
  return s;
}
export async function updateSurvey(mode: Mode, ws: Workspace, id: string, patch: { name?: string; question?: string; status?: SurveyStatus }): Promise<Survey | null> {
  if (mode === "api") { const s = await req<Survey>(`/surveys/${id}`, { method: "PATCH", body: JSON.stringify(patch) }); if (s) return s; }
  const all = localSurveys(ws);
  const i = all.findIndex((x) => x.id === id);
  if (i < 0) return null;
  all[i] = { ...all[i], ...patch };
  write(LS_SURVEYS, all);
  return all[i];
}
export async function deleteSurvey(mode: Mode, ws: Workspace, id: string): Promise<void> {
  if (mode === "api") { await req(`/surveys/${id}`, { method: "DELETE" }); return; }
  write(LS_SURVEYS, localSurveys(ws).filter((s) => s.id !== id));
  write(LS_RESPONSES, localResponses(ws).filter((r) => r.surveyId !== id));
}
export async function addResponse(mode: Mode, ws: Workspace, input: { surveyId: string; accountId: string; score: number; respondent?: string; comment?: string }): Promise<SurveyResponse> {
  if (mode === "api") { const r = await req<SurveyResponse>("/survey-responses", { method: "POST", body: JSON.stringify(input) }); if (r) return r; }
  const account = ws.accounts.find((a) => a.id === input.accountId);
  const r: SurveyResponse = { id: uid("rsp"), surveyId: input.surveyId, accountId: input.accountId, accountName: account?.name ?? "", respondent: input.respondent, score: input.score, comment: input.comment, at: new Date().toISOString() };
  write(LS_RESPONSES, [r, ...localResponses(ws)]);
  return r;
}

// ------------------------------------------------------- playbook runs ----
const ACTIONABLE = new Set(["task", "email", "meeting", "alert"]);
const localRuns = (ws: Workspace): PlaybookRun[] => read(LS_RUNS, ws.playbookRuns);
const pbOverrides = (): Record<string, Partial<Playbook>> => { try { const r = localStorage.getItem(LS_PB); if (r) return JSON.parse(r); } catch { /* ignore */ } return {}; };

/** Playbooks with local enable/run-count overrides applied (offline). */
export function playbooksWithState(ws: Workspace): Playbook[] {
  const ov = pbOverrides();
  return ws.playbooks.map((p) => (ov[p.id] ? { ...p, ...ov[p.id] } : p));
}
export async function loadPlaybookRuns(ws: Workspace): Promise<{ mode: Mode; runs: PlaybookRun[]; playbooks: Playbook[] }> {
  const [runs, pbs] = await Promise.all([req<PlaybookRun[]>("/playbook-runs"), req<Playbook[]>("/playbooks")]);
  if (runs && pbs) return { mode: "api", runs, playbooks: pbs };
  return { mode: "local", runs: localRuns(ws), playbooks: playbooksWithState(ws) };
}
export async function togglePlaybook(mode: Mode, _ws: Workspace, id: string, enabled: boolean): Promise<void> {
  if (mode === "api") { await req(`/playbooks/${id}`, { method: "PATCH", body: JSON.stringify({ enabled }) }); return; }
  const ov = pbOverrides(); ov[id] = { ...(ov[id] ?? {}), enabled };
  try { localStorage.setItem(LS_PB, JSON.stringify(ov)); } catch { /* ignore */ }
}
export interface LaunchScope { accountId: string; scopeKind?: "account" | "relationship"; scopeId?: string; scopeName?: string; planStartDate?: string; }
export async function launchPlaybook(mode: Mode, ws: Workspace, playbookId: string, opts: LaunchScope): Promise<PlaybookRun | null> {
  if (mode === "api") { const r = await req<PlaybookRun>(`/playbooks/${playbookId}/launch`, { method: "POST", body: JSON.stringify(opts) }); if (r) return r; }
  const pb = ws.playbooks.find((p) => p.id === playbookId), account = ws.accounts.find((a) => a.id === opts.accountId);
  if (!pb || !account) return null;
  const planStartDate = opts.planStartDate || "2026-08-17";
  const scheduled = scheduleRunSteps(pb.steps, planStartDate, (t) => uid(`rs_${t}`), (k) => ACTIONABLE.has(k));
  const steps: PlaybookRunStep[] = scheduled.map((rs) => {
    const tmpl = pb.steps.find((s) => s.id === rs.templateStepId);
    if (rs.status === "pending") {
      const t = materialize(ws, { title: `${rs.label} — ${account.name}`, accountId: account.id, assignee: tmpl?.assignee ?? { kind: "role", role: "csm" }, due: rs.dueDate, status: "todo", source: "playbook", priority: "high", typeId: tmpl?.taskTypeId || undefined }, uid("task"));
      write(LS_TASKS, [...read(LS_TASKS, ws.tasks), t]);
      return { ...rs, taskId: t.id };
    }
    return { ...rs };
  });
  const run: PlaybookRun = {
    id: uid("run"), playbookId, playbookName: pb.name, accountId: account.id, accountName: account.name,
    status: "active", steps, startedAt: new Date().toISOString(),
    scopeKind: opts.scopeKind ?? "account", scopeId: opts.scopeId ?? account.id, scopeName: opts.scopeName ?? account.name, planStartDate,
  };
  write(LS_RUNS, [run, ...localRuns(ws)]);
  const ov = pbOverrides(); ov[playbookId] = { ...(ov[playbookId] ?? {}), activeRuns: (ov[playbookId]?.activeRuns ?? pb.activeRuns) + 1 };
  try { localStorage.setItem(LS_PB, JSON.stringify(ov)); } catch { /* ignore */ }
  return run;
}
export async function advanceRun(mode: Mode, ws: Workspace, runId: string): Promise<PlaybookRun | null> {
  if (mode === "api") { const r = await req<PlaybookRun>(`/playbook-runs/${runId}/advance`, { method: "POST" }); if (r) return r; }
  const runs = localRuns(ws); const i = runs.findIndex((r) => r.id === runId);
  if (i < 0) return null;
  const byId = new Map(runs[i].steps.map((s) => [s.id, s]));
  const next = runs[i].steps.find((s) => s.status === "pending" && stepState(s, byId) === "eligible");
  if (next) return completeRunStepLocal(ws, runs, i, next.id);
  return runs[i];
}
/** Complete a specific step — enforces dependency + task-field gates.
 *  Returns { ok:false, reason, missing } when blocked. */
export async function completeRunStep(mode: Mode, ws: Workspace, runId: string, stepId: string, actorId?: string): Promise<{ ok: true; run: PlaybookRun } | { ok: false; reason: string; missing: TaskField[] }> {
  if (mode === "api") {
    const res = await fetch(`${API}/playbook-runs/${runId}/steps/${stepId}/complete`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ actingUserId: actorId }) });
    if (res.ok) return { ok: true, run: await res.json() as PlaybookRun };
    if (res.status === 409) { const b = await res.json().catch(() => ({})); return { ok: false, reason: b.error ?? "Blocked", missing: b.missing ?? [] }; }
    // fall through to local on other errors
  }
  const runs = localRuns(ws); const i = runs.findIndex((r) => r.id === runId);
  if (i < 0) return { ok: false, reason: "Run not found", missing: [] };
  const byId = new Map(runs[i].steps.map((s) => [s.id, s]));
  const step = byId.get(stepId);
  if (!step) return { ok: false, reason: "Step not found", missing: [] };
  if (stepState(step, byId) === "blocked") return { ok: false, reason: "Finish this step's dependencies first.", missing: [] };
  if (step.taskId) {
    const task = read(LS_TASKS, ws.tasks).find((t) => t.id === step.taskId);
    const type = task?.typeId ? read(LS_TASK_TYPES, ws.taskTypes).find((tt) => tt.id === task.typeId) : undefined;
    const missing = missingRequiredFields(type, task?.fields);
    if (missing.length) return { ok: false, reason: "Required fields are missing.", missing };
  }
  return { ok: true, run: completeRunStepLocal(ws, runs, i, stepId, actorId) };
}
function completeRunStepLocal(ws: Workspace, runs: PlaybookRun[], i: number, stepId: string, actorId?: string): PlaybookRun {
  const step = runs[i].steps.find((s) => s.id === stepId);
  runs[i].steps = runs[i].steps.map((s) => (s.id === stepId ? { ...s, status: "done" as const } : s));
  if (step?.taskId) {
    const now = new Date().toISOString();
    // mark the backing task done, stamping the completer + reassigning ownership
    const tasks = read(LS_TASKS, ws.tasks).map((t) => t.id === step.taskId ? { ...t, ...applyCompletion(t, { status: "done" }, actorId, now) } : t);
    write(LS_TASKS, tasks);
  }
  if (!runs[i].steps.some((s) => s.status === "pending")) { runs[i].status = "completed"; runs[i].completedAt = new Date().toISOString(); }
  write(LS_RUNS, runs);
  return runs[i];
}
// --- playbook template CRUD (designer) ---
export async function createPlaybook(mode: Mode, ws: Workspace, input: Partial<Playbook> & { name: string }): Promise<Playbook | null> {
  if (mode === "api") return req<Playbook>("/playbooks", { method: "POST", body: JSON.stringify(input) });
  const pb: Playbook = { id: uid("pb"), name: input.name, category: input.category ?? "Onboarding", description: input.description ?? "", trigger: input.trigger ?? "Manual", enabled: input.enabled ?? true, activeRuns: 0, completedRuns: 0, successRate: 0, scope: input.scope ?? "account", steps: input.steps ?? [] };
  write(LS_PLAYBOOKS, [pb, ...read(LS_PLAYBOOKS, ws.playbooks)]);
  return pb;
}
export async function updatePlaybookTemplate(mode: Mode, ws: Workspace, id: string, patch: Partial<Playbook>): Promise<Playbook | null> {
  if (mode === "api") return req<Playbook>(`/playbooks/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
  const rows = read(LS_PLAYBOOKS, ws.playbooks).map((p) => p.id === id ? { ...p, ...patch } : p);
  write(LS_PLAYBOOKS, rows);
  return rows.find((p) => p.id === id) ?? null;
}
export async function deletePlaybookTemplate(mode: Mode, ws: Workspace, id: string): Promise<boolean> {
  if (mode === "api") { await req(`/playbooks/${id}`, { method: "DELETE" }); return true; }
  write(LS_PLAYBOOKS, read(LS_PLAYBOOKS, ws.playbooks).filter((p) => p.id !== id));
  return true;
}
export async function loadPlaybooks(ws: Workspace): Promise<{ mode: Mode; playbooks: Playbook[] }> {
  const api = await req<Playbook[]>("/playbooks");
  return api ? { mode: "api", playbooks: api } : { mode: "local", playbooks: read(LS_PLAYBOOKS, playbooksWithState(ws)) };
}
export async function stopRun(mode: Mode, ws: Workspace, runId: string): Promise<void> {
  if (mode === "api") { await req(`/playbook-runs/${runId}/stop`, { method: "POST" }); return; }
  const runs = localRuns(ws); const i = runs.findIndex((r) => r.id === runId);
  if (i >= 0) { runs[i].status = "stopped"; write(LS_RUNS, runs); }
}

function resolveLocal(ws: Workspace, accountId: string, assignee: Task["assignee"]): string {
  if (assignee.kind === "user") return assignee.userId;
  const team = read<AccountTeam>(LS_TEAMS, ws.teams).find((t) => t.accountId === accountId) ?? ws.teams.find((t) => t.accountId === accountId);
  return team?.roles[assignee.role] || ws.accounts.find((a) => a.id === accountId)?.ownerId || ws.currentUserId;
}
